import {
  documentServerMessageSchema,
  type BlockComment,
  type BlockOp,
  type CommentBodyFormat,
  type DocBlock,
  type DocumentClientMessage,
  type DocumentPresence,
  type DocumentServerMessage,
} from '@hub/contracts'
import { buildWsRoomPath, getWsBaseUrl } from '@/lib/collab/ws-url'

/**
 * Client for a DocumentStore room (PLAN-079 §3.2). Owns the socket, the
 * authoritative block revisions, optimistic op sending with ack/reject, the
 * IndexedDB copy for instant reopen, and presence/comments. Framework-free;
 * `useDocumentClient` wraps it for React.
 */

export type DocumentStatus = 'connecting' | 'connected' | 'disconnected'

export interface DocumentClientEvents {
  /** Full document (initial load, reconnect, restore). `fromCache` = local copy, socket still syncing. */
  snapshot: (blocks: DocBlock[], fromCache: boolean) => void
  /** Ops made by someone else (a user or the agent). */
  remoteOps: (ops: BlockOp[], actor: string, turnId?: string) => void
  /** Our op was rejected: the block changed under us. */
  reject: (conflicts: Array<{ id: string; current: DocBlock | null; rev: number }>) => void
  presence: (presence: DocumentPresence[]) => void
  comments: (comments: BlockComment[]) => void
  status: (status: DocumentStatus) => void
  /** Server refused something (`comment:*` codes are translated by the UI). */
  error: (message: string) => void
}

export interface AddCommentInput {
  blockId: string
  body: string
  bodyFormat?: CommentBodyFormat
  /** Client-generated root id (`newCommentThreadId()`) so the highlight can carry it right away. */
  id?: string
  quotedText?: string | null
}

/** Client-side thread id: `thr_` + 20 base36 chars (`COMMENT_THREAD_ID_RE`). */
export function newCommentThreadId(): string {
  const bytes = new Uint8Array(16)
  crypto.getRandomValues(bytes)
  let out = ''
  for (const b of bytes) out += (b % 36).toString(36)
  return `thr_${out}`
}

const CACHE_DB = 'hub-documents'
const CACHE_STORE = 'snapshots'
const RECONNECT_BASE_MS = 500
const RECONNECT_MAX_MS = 10_000

function openCache(): Promise<IDBDatabase | null> {
  if (typeof indexedDB === 'undefined') return Promise.resolve(null)
  return new Promise((resolve) => {
    try {
      const req = indexedDB.open(CACHE_DB, 1)
      req.onupgradeneeded = () => req.result.createObjectStore(CACHE_STORE)
      req.onsuccess = () => resolve(req.result)
      req.onerror = () => resolve(null)
    } catch {
      resolve(null)
    }
  })
}

async function cacheRead(key: string): Promise<{ docRev: number; blocks: DocBlock[] } | null> {
  const db = await openCache()
  if (!db) return null
  return new Promise((resolve) => {
    try {
      const req = db.transaction(CACHE_STORE, 'readonly').objectStore(CACHE_STORE).get(key)
      req.onsuccess = () =>
        resolve((req.result as { docRev: number; blocks: DocBlock[] } | undefined) ?? null)
      req.onerror = () => resolve(null)
    } catch {
      resolve(null)
    }
  })
}

async function cacheWrite(
  key: string,
  value: { docRev: number; blocks: DocBlock[] }
): Promise<void> {
  const db = await openCache()
  if (!db) return
  try {
    db.transaction(CACHE_STORE, 'readwrite').objectStore(CACHE_STORE).put(value, key)
  } catch {
    // quota or private mode — the cache is a convenience
  }
}

export async function forgetDocumentCache(projectId: string, documentId: string): Promise<void> {
  const db = await openCache()
  if (!db) return
  try {
    db.transaction(CACHE_STORE, 'readwrite')
      .objectStore(CACHE_STORE)
      .delete(`${projectId}:${documentId}`)
  } catch {
    // ignore
  }
}

export class DocumentClient {
  readonly projectId: string
  readonly documentId: string
  blocks: DocBlock[] = []
  docRev = 0
  revs = new Map<string, number>()
  presence: DocumentPresence[] = []
  comments: BlockComment[] = []
  status: DocumentStatus = 'connecting'
  /** True once the server snapshot has been applied at least once. */
  synced = false

  #ws: WebSocket | null = null
  #closed = false
  #clientSeq = 0
  #pending = new Map<number, BlockOp[]>()
  #reconnectAttempt = 0
  #reconnectTimer: ReturnType<typeof setTimeout> | null = null
  #listeners: { [K in keyof DocumentClientEvents]: Set<DocumentClientEvents[K]> } = {
    snapshot: new Set(),
    remoteOps: new Set(),
    reject: new Set(),
    presence: new Set(),
    comments: new Set(),
    status: new Set(),
    error: new Set(),
  }

  constructor(projectId: string, documentId: string) {
    this.projectId = projectId
    this.documentId = documentId
  }

  get cacheKey() {
    return `${this.projectId}:${this.documentId}`
  }

  on<K extends keyof DocumentClientEvents>(event: K, fn: DocumentClientEvents[K]): () => void {
    this.#listeners[event].add(fn)
    return () => this.#listeners[event].delete(fn)
  }

  #emit<K extends keyof DocumentClientEvents>(
    event: K,
    ...args: Parameters<DocumentClientEvents[K]>
  ) {
    for (const fn of this.#listeners[event]) (fn as (...a: unknown[]) => void)(...args)
  }

  async connect(): Promise<void> {
    this.#closed = false
    const cached = await cacheRead(this.cacheKey)
    if (cached && !this.synced && this.blocks.length === 0) {
      this.blocks = cached.blocks
      this.docRev = cached.docRev
      this.#emit('snapshot', cached.blocks, true)
    }
    this.#open()
  }

  #open() {
    if (this.#closed) return
    this.#setStatus('connecting')
    const url = `${getWsBaseUrl()}/${buildWsRoomPath(this.projectId, 'document', this.documentId)}`
    const ws = new WebSocket(url)
    this.#ws = ws
    ws.onopen = () => {
      this.#reconnectAttempt = 0
      this.#setStatus('connected')
      // Re-send anything that was in flight when the socket dropped.
      for (const [seq, ops] of this.#pending) this.#sendRaw({ t: 'op', clientSeq: seq, ops })
    }
    ws.onmessage = (ev) => {
      let msg: DocumentServerMessage
      try {
        msg = documentServerMessageSchema.parse(JSON.parse(String(ev.data)))
      } catch (err) {
        console.error('[document-client] bad server message', err)
        return
      }
      this.#handle(msg)
    }
    ws.onclose = (ev) => {
      if (this.#ws === ws) this.#ws = null
      this.#setStatus('disconnected')
      // 1001 "… deleted" is the store telling us it is gone — do not resurrect it.
      if (ev.code === 1001 && /deleted/.test(ev.reason)) {
        this.#closed = true
        return
      }
      this.#scheduleReconnect()
    }
    ws.onerror = () => {
      // onclose follows
    }
  }

  #scheduleReconnect() {
    if (this.#closed || this.#reconnectTimer) return
    const delay = Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * 2 ** this.#reconnectAttempt++)
    this.#reconnectTimer = setTimeout(() => {
      this.#reconnectTimer = null
      this.#open()
    }, delay)
  }

  close() {
    this.#closed = true
    if (this.#reconnectTimer) clearTimeout(this.#reconnectTimer)
    this.#reconnectTimer = null
    this.#ws?.close(1000, 'closed')
    this.#ws = null
  }

  #setStatus(status: DocumentStatus) {
    if (this.status === status) return
    this.status = status
    this.#emit('status', status)
  }

  #sendRaw(msg: DocumentClientMessage) {
    if (this.#ws?.readyState === WebSocket.OPEN) this.#ws.send(JSON.stringify(msg))
  }

  #handle(msg: DocumentServerMessage) {
    switch (msg.t) {
      case 'snapshot': {
        this.blocks = msg.blocks
        this.docRev = msg.docRev
        this.revs = new Map(msg.revs.map((r) => [r.id, r.rev]))
        this.presence = msg.presence
        this.comments = msg.comments
        this.synced = true
        void cacheWrite(this.cacheKey, { docRev: msg.docRev, blocks: msg.blocks })
        this.#emit('snapshot', msg.blocks, false)
        this.#emit('presence', msg.presence)
        this.#emit('comments', msg.comments)
        break
      }
      case 'ops': {
        this.docRev = msg.docRev
        for (const r of msg.revs) this.revs.set(r.id, r.rev)
        this.blocks = applyOpsToTree(this.blocks, msg.ops)
        void cacheWrite(this.cacheKey, { docRev: msg.docRev, blocks: this.blocks })
        this.#emit('remoteOps', msg.ops, msg.actor, msg.turnId)
        break
      }
      case 'ack': {
        this.#pending.delete(msg.clientSeq)
        this.docRev = msg.docRev
        for (const r of msg.revs) this.revs.set(r.id, r.rev)
        void cacheWrite(this.cacheKey, { docRev: msg.docRev, blocks: this.blocks })
        break
      }
      case 'reject': {
        this.#pending.delete(msg.clientSeq)
        for (const c of msg.conflicts) {
          this.revs.set(c.id, c.rev)
          if (c.current)
            this.blocks = applyOpsToTree(this.blocks, [
              { kind: 'update', id: c.id, block: c.current },
            ])
        }
        this.#emit('reject', msg.conflicts)
        break
      }
      case 'presence':
        this.presence = msg.presence
        this.#emit('presence', msg.presence)
        break
      case 'comments':
        this.comments = msg.comments
        this.#emit('comments', msg.comments)
        break
      case 'error':
        console.error('[document-client]', msg.message)
        this.#emit('error', msg.message)
        break
    }
  }

  /** Send ops we made locally (already applied in the editor). */
  sendOps(ops: BlockOp[]): void {
    if (ops.length === 0) return
    this.blocks = applyOpsToTree(this.blocks, ops)
    const seq = ++this.#clientSeq
    this.#pending.set(seq, ops)
    this.#sendRaw({ t: 'op', clientSeq: seq, ops })
  }

  setEditing(blockIds: string[]): void {
    this.#sendRaw({ t: 'presence', editingBlockIds: blockIds })
  }

  addComment(input: AddCommentInput): void {
    this.#sendRaw({
      t: 'comment:add',
      blockId: input.blockId,
      body: input.body,
      bodyFormat: input.bodyFormat ?? 'text',
      id: input.id,
      quotedText: input.quotedText ?? null,
    })
  }

  replyComment(threadId: string, body: string, bodyFormat: CommentBodyFormat = 'md'): void {
    const root = this.comments.find((c) => c.id === threadId)
    this.#sendRaw({
      t: 'comment:add',
      blockId: root?.blockId ?? threadId,
      body,
      bodyFormat,
      parentId: threadId,
    })
  }

  /** (Un)resolve the thread `id` belongs to. */
  resolveThread(id: string, resolved: boolean): void {
    this.#sendRaw({ t: 'comment:resolve', id, resolved })
  }

  deleteComment(id: string): void {
    this.#sendRaw({ t: 'comment:delete', id })
  }

  currentRev(blockId: string): number | undefined {
    return this.revs.get(blockId)
  }
}

// ── pure tree helpers ─────────────────────────────────────────────────────

/** Apply ops to a block tree (immutable-ish: returns a new top-level array). */
export function applyOpsToTree(blocks: DocBlock[], ops: BlockOp[]): DocBlock[] {
  let tree = blocks.map(cloneBlock)
  for (const op of ops) {
    switch (op.kind) {
      case 'update':
        tree = mapTree(tree, (b) =>
          b.id === op.id ? { ...op.block, id: b.id, children: b.children } : b
        )
        break
      case 'delete': {
        const ids = new Set(op.ids)
        tree = filterTree(tree, (b) => !ids.has(b.id))
        break
      }
      case 'insert': {
        const ids = new Set<string>()
        for (const b of op.blocks) collect(b, ids)
        tree = filterTree(tree, (b) => !ids.has(b.id))
        tree = insertAt(tree, op.parentId, op.afterId, op.blocks.map(cloneBlock))
        break
      }
      case 'move': {
        let moved: DocBlock | null = null
        tree = filterTree(tree, (b) => {
          if (b.id === op.id) {
            moved = b
            return false
          }
          return true
        })
        if (moved) tree = insertAt(tree, op.parentId, op.afterId, [moved])
        break
      }
    }
  }
  return tree
}

function cloneBlock(b: DocBlock): DocBlock {
  return { ...b, children: b.children.map(cloneBlock) }
}

function collect(b: DocBlock, out: Set<string>) {
  out.add(b.id)
  for (const c of b.children) collect(c, out)
}

function mapTree(blocks: DocBlock[], fn: (b: DocBlock) => DocBlock): DocBlock[] {
  return blocks.map((b) => {
    const mapped = fn(b)
    return { ...mapped, children: mapTree(mapped.children, fn) }
  })
}

function filterTree(blocks: DocBlock[], keep: (b: DocBlock) => boolean): DocBlock[] {
  return blocks.filter(keep).map((b) => ({ ...b, children: filterTree(b.children, keep) }))
}

function insertAt(
  blocks: DocBlock[],
  parentId: string | null,
  afterId: string | null,
  items: DocBlock[]
): DocBlock[] {
  const insertInto = (list: DocBlock[]): DocBlock[] => {
    if (afterId === null) return [...items, ...list]
    const idx = list.findIndex((b) => b.id === afterId)
    if (idx === -1) return [...list, ...items]
    return [...list.slice(0, idx + 1), ...items, ...list.slice(idx + 1)]
  }
  if (parentId === null) return insertInto(blocks)
  return mapTree(blocks, (b) =>
    b.id === parentId ? { ...b, children: insertInto(b.children) } : b
  )
}
